import { useFormik } from "formik";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Person, LocalPhone, Email, Info, Visibility, VisibilityOff } from "@mui/icons-material";
import { CircularProgress, InputLabel, MenuItem, Select, Stack, TextField, Typography, Box } from "@mui/material";
import { Button, Checkbox, FormControlLabel, Grid } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import { validationSchema, initialValues } from "./validation";
import PasswordCritera from "./passwordCritera";
import CustomizedDialogs from "../../../../../../../../UI/footer-common-links/footerModal/footer-modal";
import { PatternFormat } from "react-number-format";
import useToggleState from "hooks/useTogglePassword";
import ApiClient from "helpers/ApiClient";
import { v4 as uuidv4 } from 'uuid';
import "./styles.scss";

//Third Party Sign up data
const thirdPartyUsersList = [
  { UserType: "Insurance Companies" },
  { UserType: "MOT Centres" },
  { UserType: "Motor Dealers" },
  { UserType: "Motor Auctioneers" },
  { UserType: "Salvage Dealers" },
  { UserType: "Finance Companies" },
  { UserType: "Fleet Operators" },
  { UserType: "Car Buying Services" },
];

const PersonalAccount = ({ setToggleSnackbar, setUserId, onSignupSuccess }: any) => {
  const { state: navigationState }: any = useLocation();

  const [signUpEmail, setSignUpEmail] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState(0);

  const [state, toggleState]: any = useToggleState({
    password: false,
    confirmPassword: false,
    passCriteria: false,
  });

  const handleOpenModal = (modalId: number) => {
    setId(modalId);
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const signUpUser = (values: any) => {
    const { email, password, firstName, lastName, phoneNumber, thirdPartyUserType }:any = values;
    setIsLoading(true);
    ApiClient.post("users/signup", {
      email,
      password,
      roles: thirdPartyUserType,
      phoneNumber,
      firstName: firstName.trim(),
      lastName:lastName.trim(),
    })
      .then((res) => {
        setIsLoading(false);
        setUserId(res.data.user.id);
        setSignUpEmail(email);
        toggleState("hideCriteria");
        setToggleSnackbar({
          message: "Check your email for verification",
          isToggle: true,
          mode: "success",
          top: "110px",
        });
        onSignupSuccess?.(email);
      })
      .catch((error: any) => {
        const err = error.response.data.message;
        setIsLoading(false);
        const defaultError = err.slice(32, 45);
        if (defaultError === "already exist") {
          setToggleSnackbar({
            message: "Email or Phone number is already registered.",
            isToggle: true,
            mode: "error",
            top: "110px",
          });
        } else {
          setToggleSnackbar({ message: err, isToggle: true, mode: "error", top: "110px" });
        }
      });
  };

  const { values, errors, touched, handleChange, handleSubmit, handleBlur } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {
      signUpUser(values);
    },
  });

  const hidePasswordCriteria = () => {
    toggleState("hideCriteria");
  };

  return (
    <Box className="personal-account">
      {state.passCriteria && <PasswordCritera value={values.password} />}
      {(
        <form onSubmit={handleSubmit}>
          <Typography variant="h5" sx={{ mb: "15px", color: "#434242", fontWeight: "500" }}>
            Account Details
          </Typography>

          <Grid container spacing={2} rowSpacing={3}>
            <Grid item xs={12}>
              <InputLabel
                htmlFor="ppcnNumber"
                className={
                  errors.thirdPartyUserType && touched.thirdPartyUserType ? "label-err" : "label"
                }
              >
                Third Party User Type <span style={{ color: 'red' }}>*</span>
              </InputLabel>

              <Select
                sx={{ width: "100%", background: "#fff" }}
                name="thirdPartyUserType"
                fullWidth
                size="small"
                variant="outlined"
                error={!!(errors.thirdPartyUserType && touched.thirdPartyUserType)}
                value={values.thirdPartyUserType}
                onChange={handleChange}
                onBlur={handleBlur}
                displayEmpty
                inputProps={{ "aria-label": "Without label" }}
              >
                {thirdPartyUsersList.map((item) => (
                  <MenuItem key={uuidv4()} value={item.UserType}>
                    {item.UserType}
                  </MenuItem>
                ))}
              </Select>
              {errors.thirdPartyUserType && touched.thirdPartyUserType && (
                <Typography sx={{ color: "#DC3545" }} variant="body2">
                  {errors.thirdPartyUserType}
                </Typography>
              )}
            </Grid>
            <Grid item md={6} xs={12}>
              <InputLabel
                htmlFor="firstName"
                className={errors.firstName && touched.firstName ? "label-err" : "label"}
              >
                First Name <span style={{ color: 'red' }}>*</span>
              </InputLabel>

              <TextField
                name="firstName"
                sx={{ background: "#fff" }}
                fullWidth
                size="small"
                variant="outlined"
                error={!!(errors.firstName && touched.firstName)}
                value={values.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={hidePasswordCriteria}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {errors.firstName && touched.firstName ? (
                        <Info sx={{ color: "#DC3545" }} fontSize="medium" />
                      ) : (
                        <Person fontSize="medium" />
                      )}
                    </InputAdornment>
                  ),
                }}
              />

              {errors.firstName && touched.firstName && (
                <Typography sx={{ color: "#DC3545" }} variant="body2">
                  {errors.firstName}
                </Typography>
              )}
            </Grid>
            <Grid item md={6} xs={12}>
              <InputLabel
                htmlFor="lastName"
                className={errors.lastName && touched.lastName ? "label-err" : "label"}
              >
                Last Name <span style={{ color: 'red' }}>*</span>
              </InputLabel>
              <TextField
                name="lastName"
                sx={{ background: "#fff" }}
                fullWidth
                size="small"
                variant="outlined"
                error={!!(errors.lastName && touched.lastName)}
                value={values.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={hidePasswordCriteria}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {errors.lastName && touched.lastName ? (
                        <Info sx={{ color: "#DC3545" }} fontSize="medium" />
                      ) : (
                        <Person fontSize="medium" />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              {errors.lastName && touched.lastName && (
                <Typography sx={{ color: "#DC3545" }} variant="body2">
                  {errors.lastName}
                </Typography>
              )}
            </Grid>
            <Grid item md={12} xs={12}>
              <InputLabel
                htmlFor="phoneNumber"
                className={errors.phoneNumber && touched.phoneNumber ? "label-err" : "label"}
              >
                Phone Number <span style={{ color: 'red' }}>*</span>
              </InputLabel>

              <PatternFormat
                displayType="input"
                format="+## ###### ####"
                customInput={TextField}
                name="phoneNumber"
                type="text"
                sx={{ background: "#fff" }}
                fullWidth
                size="small"
                variant="outlined"
                placeholder="+44 000000 0000"
                error={!!(errors.phoneNumber && touched.phoneNumber)}
                value={values.phoneNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={hidePasswordCriteria}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {errors.phoneNumber && touched.phoneNumber ? (
                        <Info sx={{ color: "#DC3545" }} fontSize="medium" />
                      ) : (
                        <LocalPhone fontSize="medium" />
                      )}
                    </InputAdornment>
                  ),
                }}
              />

              {errors.phoneNumber && touched.phoneNumber && (
                <Typography sx={{ color: "#DC3545" }} variant="body2">
                  {errors.phoneNumber}
                </Typography>
              )}
            </Grid>
            <Grid item md={12} xs={12}>
              <InputLabel
                htmlFor="email"
                className={errors.email && touched.email ? "label-err" : "label"}
              >
                Email <span style={{ color: 'red' }}>*</span>
              </InputLabel>

              <TextField
                name="email"
                type="email"
                sx={{ background: "#fff" }}
                fullWidth
                size="small"
                variant="outlined"
                error={!!(errors.email && touched.email)}
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={hidePasswordCriteria}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {errors.email && touched.email ? (
                        <Info sx={{ color: "#DC3545" }} fontSize="medium" />
                      ) : (
                        <Email fontSize="medium" />
                      )}
                    </InputAdornment>
                  ),
                }}
              />

              {errors.email && touched.email && (
                <Typography sx={{ color: "#DC3545" }} variant="body2">
                  {errors.email}
                </Typography>
              )}
            </Grid>
            <Grid item md={6} xs={12}>
              <InputLabel
                htmlFor="password"
                className={errors.password && touched.password ? "label-err" : "label"}
              >
                Password <span style={{ color: 'red' }}>*</span>
              </InputLabel>

              <TextField
                name="password"
                type={state.password ? "text" : "password"}
                sx={{ background: "#fff" }}
                fullWidth
                size="small"
                variant="outlined"
                error={!!(errors.password && touched.password)}
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={() => toggleState("showCriteria")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {state.password ? (
                        <Visibility
                          sx={{ cursor: "pointer" }}
                          fontSize="medium"
                          onClick={() => toggleState("Password")}
                        />
                      ) : (
                        <VisibilityOff
                          sx={{ cursor: "pointer" }}
                          fontSize="medium"
                          onClick={() => toggleState("Password")}
                        />
                      )}
                      {errors.password && touched.password && (
                        <Info sx={{ color: "#DC3545" }} fontSize="medium" />
                      )}
                    </InputAdornment>
                  ),
                }}
              />

              {errors.password && touched.password && (
                <Typography sx={{ color: "#DC3545" }} variant="body2">
                  {errors.password === "Required" && "Required Field"}
                </Typography>
              )}
            </Grid>
            <Grid item md={6} xs={12}>
              <InputLabel
                htmlFor="confirmPassword"
                className={
                  errors.confirmPassword && touched.confirmPassword ? "label-err" : "label"
                }
              >
                Confirm Password <span style={{ color: 'red' }}>*</span>
              </InputLabel>
              <TextField
                name="confirmPassword"
                type={state.confirmPassword ? "text" : "password"}
                sx={{ background: "#fff" }}
                fullWidth
                size="small"
                variant="outlined"
                error={!!(errors.confirmPassword && touched.confirmPassword)}
                value={values.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={hidePasswordCriteria}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {state.confirmPassword ? (
                        <Visibility
                          sx={{ cursor: "pointer" }}
                          fontSize="medium"
                          onClick={() => toggleState("confirmPassword")}
                        />
                      ) : (
                        <VisibilityOff
                          sx={{ cursor: "pointer" }}
                          fontSize="medium"
                          onClick={() => toggleState("confirmPassword")}
                        />
                      )}
                      {errors.password && touched.password && (
                        <Info sx={{ color: "#DC3545" }} fontSize="medium" />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              {errors.confirmPassword && touched.confirmPassword && (
                <Typography sx={{ color: "#DC3545" }} variant="body2">
                  {errors.confirmPassword}
                </Typography>
              )}
            </Grid>
          </Grid>
          <FormControlLabel
            className={errors.checked && touched.checked ? "error" : ""}
            sx={{ mt: "15px" }}
            label={
              <Typography variant="body1">
                I agree to
                <span className="secondary-color" onClick={() => handleOpenModal(3)}>
                  Terms & Conditions
                </span>{" "}
                and{" "}
                <span className="secondary-color" onClick={() => handleOpenModal(2)}>
                  Privacy Policy
                </span>
              </Typography>
            }
            control={
              <Checkbox
                className="error"
                name="checked"
                checked={values.checked}
                onChange={handleChange}
                sx={{
                  "&.Mui-checked": {
                    color: "#7FB341",
                  },
                }}
              />
            }
          />
          {errors.checked && touched.checked && (
            <Typography sx={{ color: "#DC3545" }} variant="body2">
              {errors.checked}
            </Typography>
          )}
          <Stack spacing={3} sx={{ textAlign: "center" }}>
            <Button
              className="btn-primary"
              sx={{ width: { xs: "100%", md: "auto" } }}
              type="submit"
              disableRipple
              variant="contained"
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} sx={{ color: "#FFFFFF", mr: 1 }} />
                  <span> Loading...</span>
                </>
              ) : (
                <span>SAVE AND CONtINUE</span>
              )}
            </Button>
            <Typography variant="body1">
              Already have an account? {""}
              <Link to="/authentication/signin" className="link">
                Sign In
              </Link>
            </Typography>
          </Stack>
        </form>
      )}
      
      <CustomizedDialogs close={handleCloseModal} open={openModal} id={id} />
    </Box>
  );
};
export default PersonalAccount;
